import { create } from 'zustand';
import { apiUrl } from '../../core/api/api-url';
import { withQuery } from '../../core/api/query';
import { appClient } from '../../core/api/http';
import { toApiError, type ApiError } from '../../core/api/api-error';
import { isMemberOf } from '../../core/api/vocabulary';
import { appConfig } from '../../core/config/app-config';
import { completedFetchSize } from '../../core/preferences/defaults';
import {
  completionsShown,
  effectiveZone,
  showCompletedProjectsInSidebar,
  showHiddenContextsInSidebar,
  stalenessStartsDays,
} from '../../core/preferences/preferences.store';
import { readUiState, writeUiState } from '../../core/storage/ui-state';
import { calendarDayIn } from '../../core/time/calendar-day';
import { clock } from '../../core/time/clock';
import { dismissRefused, submitCapture } from '../../core/outbox/outbox.store';
import { createMountGuard } from '../../core/reach/mount-guard';
import { createQuietRefresh } from '../../core/reach/quiet-refresh';
import {
  restoreLastCleared, shownFromStorageNow, useShownFromStorage, type StoredViewSummary,
} from '../../core/stored-views/presentation.store';
import { useWritable } from '../../core/stored-views/use-writable';
import type {
  ActionChanges, ActionSummary, ActionUpdateResult, ContextSummary, PagedActions, PagedContexts, PagedProjects,
  ProjectSummary, QuickCaptureResult,
} from '../../core/api/types';
import { GROUP_BY_VALUES, groupActions, holdsAction, type Group, type GroupBy, type GroupContainer } from './model/grouping';
import { deferralWarning } from '../../shared/actions/model/defer';
import { buildSidebar, type Sidebar } from './model/sidebar';
import { attempt, foldIn, type OptimisticHost, type Reread, type WriteOutcome } from '../../shared/actions/optimistic';
import { submitOrWithdraw } from '../../shared/actions/queued-change';
import { lookupsOf, type ContainerLookups } from '../../shared/containers/container-lookups';

/**
 * The API's largest page. The home page is a working list, not an archive: the practitioner is meant
 * to be able to read it, and 500 open actions is already past that. Asking for the maximum in one
 * request keeps FR-016's promise (a fixed number of requests, never one per action) and the store
 * reports `truncated` rather than silently showing a prefix of the list.
 */
const MAX_PAGE = 500;

/**
 * 024: where a drop landed — which is exactly what a group already is.
 *
 * **030: a `Group` is NO LONGER assignable to this, and that non-assignability is the whole of its
 * FR-008.** `Group` is `ContainerGroup | TagGroup`, and a tag group carries no `containerId` because
 * "file it under grocery" would mean retagging — a different write. `isContainerGroup` is the only
 * way from a `Group` to a destination.
 */
export type DropDestination = GroupContainer;

export interface HomeData {
  readonly contexts: readonly ContextSummary[];
  readonly projects: readonly ProjectSummary[];
  readonly available: readonly ActionSummary[];
  readonly deferred: readonly ActionSummary[];
  readonly completedRecent: readonly ActionSummary[];
  /** True when the practitioner has more available actions than one page can carry. */
  readonly truncated: boolean;
}

/**
 * One value, so the view never has to reason about which fields are meaningful. `error` carries the
 * last-known data so a failed refresh does not blank a page the practitioner was reading.
 */
export type HomeStatus =
  | { readonly kind: 'idle' }
  | { readonly kind: 'loading'; readonly data: HomeData | null }
  | { readonly kind: 'ready'; readonly data: HomeData }
  | { readonly kind: 'error'; readonly error: ApiError; readonly data: HomeData | null };

/**
 * What the practitioner is currently being shown, if anything. A reload and a failed reload both
 * carry the previous page forward: this list is read continuously, and blanking it to a spinner or
 * an error panel because a background refresh failed loses the practitioner's place.
 */
const dataOf = (status: HomeStatus): HomeData | null => (status.kind === 'idle' ? null : status.data);

const EMPTY: HomeData = { contexts: [], projects: [], available: [], deferred: [], completedRecent: [], truncated: false };

interface HomeState {
  readonly status: HomeStatus;
  readonly groupBy: GroupBy;
  /**
   * The practitioner's calendar day, frozen at load: a row must not change colour mid-session
   * because a timer ticked. 010 FR-006 — reduced once, in `load()`, by `calendarDayIn`, and handed
   * down; nothing below this store reduces an instant to a day of its own.
   */
  readonly today: Date;
  /** The instant `today` was reduced from, frozen with it. Staleness measures from here (010). */
  readonly now: Date;
  /** Action ids with a change in flight — shown as pending, and not changed again until it lands. */
  readonly pending: readonly number[];
  /** The last refused change. Separate from `status`, because a refused tick must not blank the page. */
  readonly problem: ApiError | null;
  /** Group keys the practitioner has folded away. Remembered across visits (FR-014). */
  readonly collapsed: readonly string[];
  /**
   * The capture box's OWN busy state, owned by the store rather than the page's
   * local React state, and independent of `status`: true from the moment `quickAdd` is called until
   * `submitCapture` itself settles, whatever the screen's own `status.kind` is doing at the time
   * (`'loading'`, `'error'`, mid-navigation-away). A page-local `useState` toggled around the same
   * `await` LOOKS equivalent but is not a property of the STORE — two calls to `quickAdd` made
   * directly (as a test does, bypassing the disabled button entirely) would both proceed to their
   * own `submitCapture`, which is exactly the double-send this flag exists to prevent regardless of
   * how the second call arrives.
   */
  readonly capturing: boolean;
}

/**
 * 030: derived from `GROUP_BY_VALUES` through `core/api/vocabulary.ts`, not spelled a second time.
 *
 * It was `value === 'context' || value === 'project'`, and adding `'tag'` to `GroupBy` does NOT make
 * that fail to compile — the guard still returns `value is GroupBy`. It simply starts refusing a
 * legitimate stored choice, so the practitioner picks Tags, reloads, and lands silently back on
 * Contexts.
 */
const isGroupBy = isMemberOf(GROUP_BY_VALUES);
const isKeyList = (value: unknown): value is string[] => Array.isArray(value) && value.every((v) => typeof v === 'string');

const store = create<HomeState>()(() => ({
  status: { kind: 'idle' }, groupBy: 'context', today: new Date(0), now: new Date(0),
  pending: [], problem: null, collapsed: [], capturing: false,
}));

// --- reading ---------------------------------------------------------------------------------

const dataNow = (): HomeData => dataOf(store.getState().status) ?? EMPTY;

export const homeStatus = { use: (): HomeStatus => store((s) => s.status), get: (): HomeStatus => store.getState().status };
export const homeData = { use: (): HomeData => store((s) => dataOf(s.status) ?? EMPTY), get: dataNow };
export const homeProblem = { use: (): ApiError | null => store((s) => s.problem), get: (): ApiError | null => store.getState().problem };
export const homeGroupBy = { use: (): GroupBy => store((s) => s.groupBy), get: (): GroupBy => store.getState().groupBy };
export const homeCollapsed = { use: (): readonly string[] => store((s) => s.collapsed), get: (): readonly string[] => store.getState().collapsed };
/** The capture box's own busy state; see `HomeState.capturing`'s own comment. */
export const homeCapturing = { use: (): boolean => store((s) => s.capturing), get: (): boolean => store.getState().capturing };

/**
 * The practitioner's calendar day, frozen at load (010 FR-006). The masthead reads it rather than
 * reducing an instant of its own, so the heading and the urgency bands can never disagree about
 * what day it is — they are the same reduction, done once, in `load()`.
 */
export const homeToday = { use: (): Date => store((s) => s.today), get: (): Date => store.getState().today };

/**
 * The rendered list. This is the only place the flat API response becomes groups, and it never sorts
 * ROWS — the server's order is the truth (FR-017).
 *
 * 030's tag axis does sort its group HEADINGS, because a tag has no server-side list to inherit an
 * order from the way contexts and projects do. Rows inside a group are still untouched.
 *
 * 034: a HOOK, because it reads two stores — this one and preferences. 009 FR-009's staleness-starts
 * is the practitioner's, read here where the call is built and passed down, so `model/grouping.ts`
 * and `shared/actions/model/staleness.ts` stay pure functions of their arguments.
 *
 * Every selector below calls its dependencies UNCONDITIONALLY. A Zustand selector is a hook, and a
 * `&&` or `||` between two of them changes the hook count between renders — the defect that cost
 * this cycle an hour in `sign-in-page.tsx`.
 */
const groupsFrom = (data: HomeData, groupBy: GroupBy, today: Date, now: Date, staleness: number): readonly Group[] =>
  groupActions({
    by: groupBy, contexts: data.contexts, projects: data.projects,
    actions: data.available, today, now, stalenessStarts: staleness,
  });

export const homeGroups = {
  use: (): readonly Group[] => {
    // Every dependency read UNCONDITIONALLY — see the note above.
    const data = homeData.use();
    const groupBy = homeGroupBy.use();
    const today = store((s) => s.today);
    const now = store((s) => s.now);
    const staleness = stalenessStartsDays.use();
    return groupsFrom(data, groupBy, today, now, staleness);
  },
  get: (): readonly Group[] => {
    const s = store.getState();
    return groupsFrom(dataNow(), s.groupBy, s.today, s.now, stalenessStartsDays.get());
  },
};

/** 016 FR-002/FR-003: the two lists the row editor's pickers derive from (see `lookupsOf`). */
export const homeLookups = {
  use: (): ContainerLookups => lookupsOf(homeData.use()),
  get: (): ContainerLookups => lookupsOf(dataNow()),
};

/**
 * The first-run case: a practitioner who has nothing yet.
 *
 * **023 changed what "nothing" means.** This asked whether the practitioner had no CONTEXTS, which
 * was a fair proxy while an account began empty. Every account is now born with an Inbox, so that
 * predicate is permanently false — the empty state it guards would have become unreachable rather
 * than merely inaccurate.
 *
 * It asks about ACTIONS now, which is the condition it was always trying to express: has this
 * practitioner captured anything? Deferred and completed count, because a practitioner with a
 * tickler full of things has plainly started.
 *
 * **The name is the point.** It used to be `isFirstRun`, which claimed to answer "has this
 * practitioner started?" — a question no snapshot can answer. Named for what it actually computes —
 * this page has nothing to show — the caveats stop being caveats.
 */
const nothingIn = (data: HomeData): boolean =>
  data.available.length === 0 && data.deferred.length === 0 && data.completedRecent.length === 0;

export const nothingCapturedYet = {
  use: (): boolean => nothingIn(homeData.use()),
  get: (): boolean => nothingIn(dataNow()),
};

/**
 * FR-013: how much is waiting in each container, in the reference's phrasing (research R-005).
 *
 * 009 FR-010: which containers are listed is the practitioner's, read here. `useHomeGroups` is
 * deliberately not given the same treatment — sidebar visibility must not be able to change what a
 * group contains, and the only way to guarantee that is for grouping never to see it.
 */
const sidebarFrom = (data: HomeData, showCompletedProjects: boolean, showHiddenContexts: boolean): Sidebar =>
  buildSidebar(data.contexts, data.projects, data.available, data.deferred, { showCompletedProjects, showHiddenContexts });

export const homeSidebar = {
  use: (): Sidebar =>
    sidebarFrom(homeData.use(), showCompletedProjectsInSidebar.use(), showHiddenContextsInSidebar.use()),
  get: (): Sidebar =>
    sidebarFrom(dataNow(), showCompletedProjectsInSidebar.get(), showHiddenContextsInSidebar.get()),
};

/**
 * 034: every derived value is a `{ use, get }` PAIR, like the session and preferences stores'. The
 * first port made them hooks only, which reads as idiomatic React right up until a spec — or any
 * code outside a component — needs the value and cannot legally call a hook to get it. The shared
 * `…From` helper under each pair is what stops the two halves drifting into different answers.
 */

// --- writing ---------------------------------------------------------------------------------

const groupByKey = (): string => `${appConfig().storagePrefix}home.groupBy`;
const collapsedKey = (): string => `${appConfig().storagePrefix}home.collapsed`;
const actionsUrl = (): string => apiUrl('/api/v1/actions');
const contextsUrl = (): string => withQuery(apiUrl('/api/v1/contexts'), { pageSize: MAX_PAGE });
const projectsUrl = (): string => withQuery(apiUrl('/api/v1/projects'), { pageSize: MAX_PAGE, state: 'all' });

type ActionsView = 'available' | 'deferred' | 'completed-recent';

/**
 * The ONE construction of an actions-view URL — `readActions` below and `homeViewUrls()` (036,
 * issue 208) both call this rather than each building the query string its own way, which is what
 * keeps `core/stored-views/`'s allow-list matching the exact request this store actually makes.
 */
const actionsViewUrl = (view: ActionsView): string =>
  view === 'completed-recent'
    // 009 FR-007: one preference governs how many completions every screen shows, and the fetch
    // holds a little more than that so "show more" has something to reveal (`completedFetchSize`).
    ? withQuery(actionsUrl(), { view, pageSize: completedFetchSize(completionsShown.get()) })
    : withQuery(actionsUrl(), { view, pageSize: MAX_PAGE });

/**
 * 036 (issue 208): `confirmingWrite` sets `requireLiveAnswer` — every caller CONFIRMING a write
 * that just happened (a re-read after a tick, a move, a capture) passes `true`, so a refusal there
 * surfaces FR-010's existing banner rather than being masked by a stored view that predates the
 * write. `readHomePage()` below is the one caller that means "draw the readable screen" and leaves
 * it `false`.
 */
const readActions = (view: ActionsView, confirmingWrite = false): Promise<PagedActions> =>
  appClient().get<PagedActions>(actionsViewUrl(view), { requireLiveAnswer: confirmingWrite });

/**
 * 036 (issue 208, research R-008) — the exact five URLs `readHomePage()` requests, for
 * `core/stored-views/`'s allow-list and for the page's own "is any of MY data shown from storage"
 * question (`useShownFromStorage`/`useWritable`). Built from the SAME helpers `readHomePage()` and
 * `readActions()` use, so this can never name a URL home does not actually request.
 */
export function homeViewUrls(): readonly string[] {
  return [
    contextsUrl(),
    projectsUrl(),
    actionsViewUrl('available'),
    actionsViewUrl('deferred'),
    actionsViewUrl('completed-recent'),
  ];
}

/**
 * 036 (issue 208, FR-018, FR-024) — whether any of home's own five reads is currently drawn from
 * storage, and the OLDEST such answer's moment and reason (contract: "where a screen is made of
 * several answers, the oldest is shown"). The page's stored-list line reads this directly.
 */
export const homeStoredView = {
  use: (): StoredViewSummary => useShownFromStorage(homeViewUrls()),
  get: (): StoredViewSummary => shownFromStorageNow(homeViewUrls()),
};

/**
 * FR-035, research R-022 — false exactly while `homeStoredView` says a stored view is showing. Every
 * control FR-026 does not name is rendered under this on home.
 */
export const useHomeWritable = (): boolean => useWritable(homeViewUrls());

/**
 * A capture refused while the home page is still the one showing it can be shown there and removed
 * from the outbox (`dismissRefused`); one refused after the practitioner navigated away must not
 * surface `problem` on a screen the line was never typed on (`core/reach/mount-guard.ts`). Home has
 * no per-container identity to track — it is mounted, or it is not.
 */
const homeMountGuard = createMountGuard<undefined>();
export const registerHomeMounted = homeMountGuard.register;

/** `store.setState`'s ready-data half, with no side effect on the quiet-refresh generation — the one
 *  thing that is allowed to apply a background answer without invalidating another one in flight. */
const applyReady = (next: HomeData): void => store.setState({ status: { kind: 'ready', data: next } });

/**
 * `core/reach/quiet-refresh.ts` — see that file for the mechanism. `setData` (below) is every LOCAL
 * change this store makes to its own data; `readHomePage` is the one read `load()` and a quiet
 * refresh both build.
 */
const quietRefresh = createQuietRefresh<HomeData>({
  ready: () => store.getState().status.kind === 'ready',
  busy: () => store.getState().pending.length > 0,
  read: () => readHomePage(),
  apply: applyReady,
  busyDiscard: () => restoreLastCleared(homeViewUrls()),
});

/** Every local change to the screen's data goes through this, so the quiet refresh always knows one
 *  happened — a capture folding in its new container, an edit landing, a move, all of it. */
const setData = (next: HomeData): void => {
  quietRefresh.touched();
  applyReady(next);
};

/**
 * Adapts this store to `shared/actions/optimistic.ts`'s write shape (research R-008; T023). The
 * write itself — optimistic step, call, settle/rollback, per-id `pending`, re-read — moved there
 * unchanged (`home.store.writes.spec.ts` is the regression proof); only the plumbing lives here.
 */
const writeHost: OptimisticHost<HomeData> = {
  isReady: () => store.getState().status.kind === 'ready',
  pending: () => store.getState().pending,
  data: dataNow,
  setData,
  begin: (id) => {
    store.setState({ pending: [...store.getState().pending, id], problem: null });
  },
  fail: (problem) => store.setState({ problem }),
  release: (id) => {
    store.setState({ pending: store.getState().pending.filter((p) => p !== id) });
    quietRefresh.settled();
  },
};

/** The three re-reads a write can invalidate, and the fold each one applies to `HomeData`. Each
 *  CONFIRMS a write that just happened, so each asks `readActions` for a live answer only. */
const rereadAvailable: Reread<HomeData, PagedActions> = {
  read: () => readActions('available', true),
  apply: (data, { items, total }) => ({ ...data, available: items, truncated: total > items.length }),
};
const rereadDeferred: Reread<HomeData, PagedActions> = {
  read: () => readActions('deferred', true),
  apply: (data, { items }) => ({ ...data, deferred: items }),
};
const rereadCompletedRecent: Reread<HomeData, PagedActions> = {
  read: () => readActions('completed-recent', true),
  apply: (data, { items }) => ({ ...data, completedRecent: items }),
};

/**
 * Since 012 the answer is the `{ action, spawned }` envelope, not a bare action: completing the open
 * occurrence of a recurring series creates its successor in the same transaction and returns it
 * here. `spawned` is null for every other write, and the type says so rather than leaving it
 * optional (011 R-014).
 */
const patchAction = (id: number, body: ActionChanges): Promise<ActionUpdateResult> =>
  appClient().patch<ActionUpdateResult>(`${actionsUrl()}/${id}`, body);

const replace = (data: HomeData, action: ActionSummary): HomeData => ({
  ...data,
  available: data.available.map((a) => (a.id === action.id ? action : a)),
});

export function setGroupBy(by: GroupBy): void {
  store.setState({ groupBy: by });
  writeUiState(groupByKey(), by);
}

/**
 * Folding a group away. Remembered by key rather than by index, so it survives a context being
 * renamed, reordered, or added (FR-014).
 */
export function toggleCollapsed(key: string): void {
  const current = store.getState().collapsed;
  const collapsed = current.includes(key) ? current.filter((k) => k !== key) : [...current, key];
  store.setState({ collapsed });
  writeUiState(collapsedKey(), collapsed);
}

export const isCollapsed = (key: string): boolean => store.getState().collapsed.includes(key);

/** Restores the remembered choices. Called once, by the page, before the first render. */
export function restoreHomePreferences(): void {
  store.setState({
    groupBy: readUiState(groupByKey(), isGroupBy) ?? 'context',
    collapsed: readUiState(collapsedKey(), isKeyList) ?? [],
  });
}

/**
 * Folds back the action a tag write returned (011 FR-024). The write itself belongs to the row's
 * editor; this only puts the server's answer where the old row was.
 *
 * **Nothing is re-read, deliberately.** No ordering rule and no availability rule reads a tag.
 */
export function adoptAction(updated: ActionSummary): void {
  const status = store.getState().status;
  if (status.kind !== 'ready') return;
  setData(replace(status.data, updated));
}

export const isPending = (id: number): boolean => store.getState().pending.includes(id);

/**
 * 036 T092 (real finding, not asked for by the issue) — a REAL subscription for `pending`, read once
 * per page render rather than per row: `isPending(id)` above is read at render time by every screen,
 * and every call site that does that never subscribed to it (the same "reads `pending` without
 * subscribing" trap issue 209 names for the overlay, already live here before this cycle). `mutate`'s own
 * optimistic step happened to move OTHER state (`data`) the page WAS subscribed to in the same tick,
 * so the page re-rendered anyway and picked up the fresh `pending` array as a side effect. `submit`'s
 * own optimistic step is skipped entirely while not reachable (`optimistic.ts`'s proven behaviour),
 * so a write made through it with no side effect on `data` left the row's disabled/pending styling
 * stuck stale until something else re-rendered the page — caught by
 * `container-page.spec.tsx`'s existing "marks a row pending" test going red under 036's own change,
 * not by a new test written for this. Returns the array so a caller's `.map()` over rows can check
 * `.includes(id)` per row without a hook per row, which the Rules of Hooks forbid.
 */
export const usePendingIds = (): readonly number[] => store((s) => s.pending);

export function dismissProblem(): void {
  store.setState({ problem: null });
}

/**
 * Completing or reopening (036 T092). Routed through the outbox so a tick is accepted with no
 * connection (issue 209) — `queued-change.ts`'s own header comment has the two review findings this
 * closes. **The row is left exactly where it is**, with no optimistic move: constitution II / issue 209's
 * rule is that a row shows the practitioner's own change and nothing that follows from it, and the
 * ticked appearance is `action-row.tsx`'s overlay (T091, `useUnsentFor`), not a change to `available`
 * or `completedRecent` here. Once the write actually lands, both lists are re-read — where a
 * completed or reopened action belongs is the server's decision (FR-017), never a guess made here.
 */
export function setCompleted(action: ActionSummary, completed: boolean): Promise<void> {
  return submitOrWithdraw(
    writeHost,
    action.id,
    'isCompleted',
    { isCompleted: completed },
    { isCompleted: completed, description: action.description },
    (current) => current,
    // A spawned occurrence can be deferred, which is the one list a completion otherwise never
    // affects — so the tickler joins the re-read exactly when the server says it spawned one.
    ({ spawned }) => spawned
      ? [rereadAvailable, rereadCompletedRecent, rereadDeferred]
      : [rereadAvailable, rereadCompletedRecent],
  ).then(() => undefined);
}

/**
 * 016 FR-018: the row's editor has saved one of the five fields. The PATCH is **its**, not this
 * store's — a refusal has to appear beside the form the practitioner is still looking at (FR-010) —
 * so what arrives here is a write that already succeeded. `foldIn` rather than `mutate` for exactly
 * that reason: there is nothing to be optimistic about and, more importantly, `mutate` refuses a row
 * that is already `pending`, which would silently discard a committed edit.
 *
 * `adoptAction` is deliberately **not** reused. It exists for tag writes, where nothing is re-read.
 * All three of context, project and due date feed `NextActionRepository.ViewCte`, so an edited
 * action can leave the list, join the tickler, or simply sort elsewhere — and where it belongs is
 * the server's (006 FR-017, 016 FR-019).
 */
export function editSaved(updated: ActionSummary): Promise<void> {
  return foldIn(writeHost, updated.id, (data) => replace(data, updated), [rereadAvailable, rereadDeferred]);
}

/**
 * 024 FR-005/FR-006: an action was dropped on a group, so it joins that group's container.
 *
 * **This is `editSaved`'s write with the PATCH still to send.** Same endpoint, same fields, same
 * re-read list — precedent rather than economy. The re-read is what makes SC-008 true: dropping into
 * a completed project makes an action unavailable, and the action's own record does not say so,
 * because availability is computed across the list query (`ViewCte`) and is not carried on the row.
 *
 * A same-group drop never reaches here — the page returns before calling (FR-008) — but the guard is
 * repeated because a store method that silently rewrites an action to where it already is would
 * issue a request and a re-read for nothing.
 */
export function moveToContainer(action: ActionSummary, destination: DropDestination): Promise<WriteOutcome> {
  // The same predicate the picker filters its destinations with (`holdsAction`), so the two can
  // never disagree about which entry would write nothing.
  if (holdsAction(destination, action)) return Promise.resolve('not-attempted');

  // No cast. `DropDestination` is a discriminated union, so `contextId` is `number` in the branch
  // that sends it and there is no way to spell `{ contextId: null }` — which is what FR-007's doc
  // comment claims, and what an earlier `as number` quietly defeated.
  const body: ActionChanges = destination.kind === 'context'
    ? { contextId: destination.containerId }
    : { projectId: destination.containerId };

  /**
   * **`attempt`, not `mutate` — this write's own outcome rather than the page's.**
   *
   * `problem` is a PAGE-level signal and `begin` clears it, so reading it after a write settles is
   * only correct while exactly one write is in flight. The picker makes that false by design: the
   * menu closes the instant a destination is chosen, so the next row's control is live while the
   * previous write is still going. A concurrent refusal would then be reported against the wrong
   * action. `fail` is called on **two** different exits, and only the port knows which it took.
   */
  return attempt(
    writeHost,
    action.id,
    (data) => data,                       // nothing optimistic: where it lands is the server's
    (data) => data,
    () => patchAction(action.id, body),
    (data, { action: updated }) => replace(data, updated),
    // **Available only.** The deferred view's predicate is `completed_at IS NULL AND
    // defer_until > @today` — no `project_ok`, no `context_ok` — so a container change cannot add to
    // it or remove from it. Re-reading only what a write can invalidate is this store's rule.
    [rereadAvailable],
  );
}

/**
 * A star marks an action (036 T092, routed through the outbox); it changes neither its order nor its
 * availability, so nothing is re-read — the server's own answer, once it arrives, is folded straight
 * in. No optimistic move (`(data) => data`): the starred appearance while unsent is the overlay's
 * (T091), same reasoning as `setCompleted` above.
 */
export function toggleStar(action: ActionSummary): Promise<void> {
  const isStarred = !action.isStarred;
  return submitOrWithdraw(
    writeHost,
    action.id,
    'isStarred',
    { isStarred },
    { isStarred },
    (data, { action: updated }) => replace(data, updated),
    [],
  ).then(() => undefined);
}

/**
 * Deferring (036 T092, routed through the outbox) takes an action out of the list until its date —
 * once the write actually lands — so both affected views are re-read then; no optimistic move here
 * either, for the same reason `setCompleted` has none.
 */
export function defer(action: ActionSummary, deferUntil: string | null): Promise<void> {
  return submitOrWithdraw(
    writeHost,
    action.id,
    'deferUntil',
    { deferUntil },
    { deferUntil },
    (data) => data,
    [rereadAvailable, rereadDeferred],
  ).then(() => undefined);
}

/** FR-011, as a pure rule the row can also apply before anything is sent (model/defer.ts). */
export { deferralWarning };

/**
 * One line of Tracks markup (FR-008). Quick-add reports the context or project it invented, so a new
 * heading appears without a reload; the list itself is re-read because where the new action sits in
 * the order is the server's decision.
 *
 * **036 (issue 207): routed through `core/outbox/`'s `submit()`, the only door to this route
 * (`only-door.spec.ts`).** No longer conditioned on the screen's own `status` — research F-005 named
 * that early return as a silent no-op today, and there is now somewhere for a capture typed while a
 * load is failing to go. Three of `submit`'s four outcomes are handled here:
 *
 * - `answered`: unchanged from before this cycle — re-read, merge the invented container, done.
 * - `waiting`: nothing is re-read and no `problem` is set. The field's own clearing is the caller's
 *   (`home-page.tsx` clears it whenever `homeProblem` stayed null), so a capture made with no
 *   connection behaves exactly as position 6 describes without this function doing anything special.
 * - `refused`: `problem` is set exactly as it always was.
 *
 * A device that will not store anything is `submit`'s own concern (FR-013): it sends directly and
 * either answers or throws, so the `catch` below is unchanged from what it caught before this cycle.
 *
 * **The capture itself is ALWAYS submitted, whatever the screen's status — but
 * an `answered` capture's re-read is only ever APPLIED while the store is still `ready`.** Without
 * this, a capture answered while the load is in `error` called `setData`, which unconditionally
 * sets `status: 'ready'` — silently curing the error, replacing whatever the practitioner was
 * looking at with a page built from `EMPTY` plus one fresh pair of reads, and taking the Retry
 * control with it. The field still clears (nothing here changes that) and the problem is still
 * shown for a genuine refusal; only an `answered` capture's side effect on a non-`ready` screen is
 * withheld.
 *
 * **`capturing` guards this function itself, not merely the page's own button:**
 * a second call arriving while one capture is still being WRITTEN is a no-op, whatever `status.kind`
 * is at the time. Set the instant this function actually commits to a capture, and cleared as soon
 * as the line is DURABLY on the device (`submitCapture`'s own `onQueued`, issue 257 A3) — never held
 * for however long the send itself takes.
 *
 * **036 (issue 257, A3) — the box used to stay busy until `submitCapture` settled, which on a
 * stalled connection (accepted, never answered) is up to `SEND_TIMEOUT_MS` plus the next pass.**
 * `capturing` now clears the MOMENT the line is durably queued; the send outcome — `refused` shows
 * the problem and dismisses the row, `answered` re-reads the list — still runs, just without holding
 * the box shut while it waits. `queued` tracks whether `onQueued` actually fired, so the `finally`
 * below still clears `capturing` on the FR-013 fallback path (the device would not store anything at
 * all, so there is nothing to call `onQueued` for) and on any exception thrown before it could.
 */
export async function quickAdd(line: string): Promise<void> {
  const text = line.trim();
  if (text.length === 0) return;
  if (store.getState().capturing) return;

  store.setState({ problem: null, capturing: true });
  let queued = false;
  let created: QuickCaptureResult;
  try {
    const outcome = await submitCapture<QuickCaptureResult>({ line: text }, {
      onQueued: () => {
        queued = true;
        store.setState({ capturing: false });
      },
    });
    if (outcome.kind === 'waiting') return;
    if (outcome.kind === 'refused') {
      // Shown here, and the row removed from the outbox, only while this is still the screen the
      // line was typed on — otherwise the practitioner would never see the reason, and the row stays
      // refused so the unsent count still tells them (`core/reach/mount-guard.ts`).
      if (homeMountGuard.isCurrent(undefined)) {
        store.setState({ problem: outcome.problem });
        void dismissRefused(outcome.seq, outcome.practitionerId);
      }
      return;
    }
    created = outcome.result;
  } catch (error) {
    store.setState({ problem: toApiError(error) });
    return;
  } finally {
    if (!queued) store.setState({ capturing: false });
  }

  if (store.getState().status.kind !== 'ready') return; // nothing left to refresh into

  try {
    // Both views: a line carrying `>date` lands in neither the list nor nowhere — it lands in the
    // deferred set, and the sidebar has to say so rather than read as empty (FR-013).
    // Confirming the capture that just landed — a live answer only, for the same reason the other
    // re-reads above ask for one.
    const [available, deferred] = await Promise.all([readActions('available', true), readActions('deferred', true)]);
    if (store.getState().status.kind !== 'ready') return; // the screen could have left `ready` mid-read
    const data = dataNow();
    setData({
      ...data,
      contexts: created.createdContext ? [...data.contexts, created.createdContext] : data.contexts,
      projects: created.createdProject ? [...data.projects, created.createdProject] : data.projects,
      available: available.items,
      deferred: deferred.items,
      truncated: available.total > available.items.length,
    });
  } catch (error) {
    if (store.getState().status.kind === 'ready') store.setState({ problem: toApiError(error) });
  }
}

/**
 * The ONE read `load()` and `quietRefreshHome()` both build. Before this fix
 * each wrote out the identical five requests and the identical `truncated` calculation a second
 * time; `only-request-path-once.spec.ts` is what now fails if either grows a second, independent
 * copy of one of these URLs rather than a second CALL to this function.
 */
async function readHomePage(): Promise<HomeData> {
  const [contexts, projects, available, deferred, completedRecent] = await Promise.all([
    appClient().get<PagedContexts>(contextsUrl()),
    appClient().get<PagedProjects>(projectsUrl()),
    readActions('available'),
    readActions('deferred'),
    readActions('completed-recent'),
  ]);
  return {
    contexts: contexts.items,
    projects: projects.items,
    available: available.items,
    deferred: deferred.items,
    completedRecent: completedRecent.items,
    truncated: available.total > available.items.length,
  };
}

/**
 * The whole page, in five fixed requests (research R-003) — never one per action (FR-016). They are
 * issued together: they do not depend on each other, and the page is only useful when they have all
 * arrived.
 */
export async function load(): Promise<void> {
  // A fresh load supersedes anything a quiet refresh has in flight.
  quietRefresh.touched();
  // Carried forward so neither the reload nor a failed reload blanks the page.
  const carried = dataOf(store.getState().status);
  // 010 FR-006: one reduction, here. `calendarDayIn` is the application's only instant→day
  // function; everything downstream takes the day as a parameter.
  const instant = clock();
  store.setState({
    status: { kind: 'loading', data: carried },
    today: calendarDayIn(instant, effectiveZone.get()), now: instant,
  });
  try {
    const data = await readHomePage();
    applyReady(data);
  } catch (error) {
    store.setState({ status: { kind: 'error', error: toApiError(error), data: carried } });
  }
}

/**
 * The QUIET re-read `resync` (FR-036) must trigger, not `load()` itself. `load()` sets `status` to
 * `loading` while it runs; while `loading` the write host is not ready
 * (`shared/actions/optimistic.ts`), so a tick, star or defer clicked in that moment was silently
 * dropped with no error, and the sidebar's collapsed groups reset because `load()` re-derives them
 * from scratch. This fetches exactly what `load()` fetches, and replaces the data ONLY once every
 * request has answered — never touching `status`'s kind, `today`/`now` (frozen at the real load, on
 * purpose — FR-006), `pending`, `problem`, `collapsed`, or `groupBy`. Runs only when `status` is
 * already `ready`: a resync tick before the first real load, or arriving during one already in
 * flight, has nothing of this shape to refresh into.
 *
 * A failure here is swallowed entirely (`archive.store.ts`'s `resyncArchive` surfaces its own
 * `problem` on failure — a choice available to it because `problem` already means "the last WRITE
 * this store attempted was refused"; extending that to a background read the practitioner never
 * asked for would misuse the same banner for two different facts). The data already on screen stays
 * exactly as correct as it was a moment ago; the next resync tick, or the next real `load()`, tries
 * again. See `core/reach/quiet-refresh.ts` for the generation mechanism behind all of that.
 */
export const quietRefreshHome = quietRefresh.run;

/** Test seam, like the other stores'. Production code never calls it. */
export function resetHome(): void {
  store.setState({
    status: { kind: 'idle' }, groupBy: 'context', today: new Date(0), now: new Date(0),
    pending: [], problem: null, collapsed: [], capturing: false,
  });
  quietRefresh.reset();
  homeMountGuard.reset();
}
